/**
 * Production adapter for `clear_contours`: binds the React-free ports
 * ({@link ClearContoursDeps}) to the SAME engine calls the right-hand panel's
 * Clear contours operation uses (`Environment.clearContourOnSlice` with the
 * main view's slice position, `clearAllContours`, `keepEveryNthSlice`), so
 * the agent path clears exactly what the panel would and records the same
 * undo step. The engine is a SYSTEM BOUNDARY: this glue is verified by the
 * manual demo; the logic it feeds is unit-tested through fakes.
 */
import { useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import { environmentState, mainViewState } from '~/state/atoms/engineAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { projectStructureFacts } from '../core/engineStructureFacts';
import type { ClearContoursDeps } from './toolRunner';

export function useClearContoursEngineDeps(): () => Omit<
    ClearContoursDeps,
    'pendingMutationExists'
> {
    const env = useRecoilValue(environmentState);
    const mainView = useRecoilValue(mainViewState);
    const structureSet = useRecoilValue(currentStructureSetState);

    return useCallback(
        () => ({
            structures: projectStructureFacts(structureSet, env),
            canModify: (roiNumber: number) =>
                env?.getRoi(roiNumber)?.canModify ?? false,
            // The panel's "current" scope reads the same coordinate.
            currentSlicePositionMm: () => mainView?.position[2],
            // Read live: `env` is the mutable singleton.
            currentContextId: () => env?.structureSet?.sopInstanceUID,
            volumeCm3: (roiNumber: number) =>
                env?.getRoiVolumeCm3(roiNumber) ?? Number.NaN,
            clear: {
                slice: (roiNumber: number, slicePositionMm: number) => {
                    requireEnv(env).clearContourOnSlice(
                        roiNumber,
                        slicePositionMm
                    );
                },
                all: (roiNumber: number) => {
                    requireEnv(env).clearAllContours(roiNumber);
                },
                keepEveryNth: (roiNumber: number, n: number) => {
                    requireEnv(env).keepEveryNthSlice(roiNumber, n);
                },
            },
        }),
        [env, mainView, structureSet]
    );
}

function requireEnv<E>(env: E | null): E {
    if (!env) {
        throw new Error('no image is loaded in the viewer');
    }
    return env;
}
